import React, { useState } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
} from '@mui/material';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  AreaChart,
  Area,
  PieChart,
  Pie,
  Cell,
  Legend,
} from 'recharts';

// Mock data for charts
const weeklyData = [
  { day: 'MON', value: 120 },
  { day: 'TUE', value: 140 },
  { day: 'WED', value: 130 },
  { day: 'THU', value: 150 },
  { day: 'FRI', value: 80 },
  { day: 'SAT', value: 60 },
  { day: 'SUN', value: 100 },
];

const monthlyData = Array.from({ length: 30 }, (_, i) => ({
  day: i + 1,
  value: Math.floor(Math.random() * 2000) + 500,
}));

const pieData = [
  { name: 'Value - 01 (It)', value: 12423, color: '#1976d2' },
  { name: 'Value - 02 (It)', value: 12423, color: '#ffc107' },
  { name: 'Value - 03 (It)', value: 8000, color: '#ff9800' },
];

const SpeedMonitoringPage: React.FC = () => {
  const [typeSelector, setTypeSelector] = useState('type1');
  const [timeSelector, setTimeSelector] = useState('monthly');

  return (
    <Box>
      <Typography variant="h4" gutterBottom>
        Speed Explanation
      </Typography>

      {/* Top Bar Chart */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Weekly Performance
          </Typography>
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={weeklyData}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="day" />
              <YAxis domain={[0, 150]} />
              <Tooltip />
              <Bar dataKey="value" fill="#1976d2" />
            </BarChart>
          </ResponsiveContainer>
        </CardContent>
      </Card>

      {/* Middle Section with Metrics and Area Chart */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        {/* Metrics Cards */}
        <Grid item xs={12} md={4}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Key Metrics
              </Typography>
              <Box sx={{ mb: 2 }}>
                <Typography variant="h4" color="primary">
                  89,529
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Total Data (01)
                </Typography>
              </Box>
              <Box sx={{ mb: 2 }}>
                <Typography variant="h4" color="secondary">
                  83,529
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Data A (02)
                </Typography>
              </Box>
              <Box>
                <Typography variant="h4" color="warning.main">
                  5,000
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Data B (03)
                </Typography>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* Area Chart */}
        <Grid item xs={12} md={8}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Trend Analysis
              </Typography>
              <ResponsiveContainer width="100%" height={300}>
                <AreaChart data={monthlyData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="day" />
                  <YAxis domain={[0, 2500]} />
                  <Tooltip />
                  <Area 
                    type="monotone" 
                    dataKey="value" 
                    stroke="#1976d2" 
                    fill="#1976d2" 
                    fillOpacity={0.3} 
                  />
                </AreaChart>
              </ResponsiveContainer>
              
              {/* Selectors */}
              <Box sx={{ mt: 2, display: 'flex', gap: 2 }}>
                <FormControl size="small" sx={{ minWidth: 120 }}>
                  <InputLabel>Type Selector</InputLabel>
                  <Select
                    value={typeSelector}
                    label="Type Selector"
                    onChange={(e) => setTypeSelector(e.target.value)}
                  >
                    <MenuItem value="type1">Type 1</MenuItem>
                    <MenuItem value="type2">Type 2</MenuItem>
                    <MenuItem value="type3">Type 3</MenuItem>
                  </Select>
                </FormControl>
                
                <FormControl size="small" sx={{ minWidth: 120 }}>
                  <InputLabel>Time Selector</InputLabel>
                  <Select
                    value={timeSelector}
                    label="Time Selector"
                    onChange={(e) => setTimeSelector(e.target.value)}
                  >
                    <MenuItem value="daily">Daily</MenuItem>
                    <MenuItem value="weekly">Weekly</MenuItem>
                    <MenuItem value="monthly">Monthly</MenuItem>
                  </Select>
                </FormControl>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Bottom Section with Pie Chart */}
      <Grid container spacing={3}>
        <Grid item xs={12} md={8}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Data Distribution
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Overview of data categories and their values
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        
        <Grid item xs={12} md={4}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Distribution Chart
              </Typography>
              <ResponsiveContainer width="100%" height={250}>
                <PieChart>
                  <Pie
                    data={pieData}
                    cx="50%"
                    cy="50%"
                    innerRadius={40}
                    outerRadius={80}
                    paddingAngle={5}
                    dataKey="value"
                  >
                    {pieData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.color} />
                    ))}
                  </Pie>
                  <Tooltip />
                  <Legend />
                </PieChart>
              </ResponsiveContainer>
              
              {/* Legend Items */}
              <Box sx={{ mt: 2 }}>
                {pieData.map((item, index) => (
                  <Box key={index} sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                    <Box
                      sx={{
                        width: 12,
                        height: 12,
                        backgroundColor: item.color,
                        borderRadius: '50%',
                        mr: 1,
                      }}
                    />
                    <Typography variant="body2">
                      {item.name} - {item.value.toLocaleString()}
                    </Typography>
                  </Box>
                ))}
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
};

export default SpeedMonitoringPage;
